import { Link } from "react-router-dom";

interface CalcHeroProps {
  calcType: "home" | "office";
  onTypeChange: (type: "home" | "office") => void;
}

const floatingItems = [
  { icon: "ri-sofa-line",       top: "18%",  left: "6%",   size: 52, color: "#6CC412", delay: "0s",    cls: "anim-float" },
  { icon: "ri-delete-bin-line", top: "12%",  right: "8%",  size: 44, color: "#FEE2E2", delay: "0.3s",  cls: "anim-float-b" },
  { icon: "ri-fridge-line",     top: "62%",  left: "3%",   size: 48, color: "#E0F2FE", delay: "0.7s",  cls: "anim-float-c" },
  { icon: "ri-hotel-bed-line",  top: "72%",  right: "5%",  size: 56, color: "#EDE9FE", delay: "1s",    cls: "anim-float" },
  { icon: "ri-tv-line",         top: "40%",  right: "2%",  size: 40, color: "#FEF3C7", delay: "0.5s",  cls: "anim-float-b" },
  { icon: "ri-truck-line",      top: "30%",  left: "1%",   size: 38, color: "#6CC412", delay: "1.4s",  cls: "anim-float-c" },
];

export default function CalcHero({ calcType, onTypeChange }: CalcHeroProps) {
  return (
    <section
      className="relative overflow-hidden pt-36 pb-20"
      style={{ background: "#09245b" }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: "radial-gradient(circle, #6CC412 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />

      {/* Floating junk items */}
      {floatingItems.map((fi, i) => (
        <div
          key={i}
          className={`absolute hidden lg:flex items-center justify-center rounded-2xl thick-border opacity-80 ${fi.cls}`}
          style={{
            top: fi.top,
            left: "left" in fi ? fi.left : undefined,
            right: "right" in fi ? fi.right : undefined,
            width: fi.size,
            height: fi.size,
            background: fi.color,
            animationDelay: fi.delay,
            zIndex: 1,
          }}
        >
          <i className={`${fi.icon} text-cl-dark`} style={{ fontSize: fi.size * 0.4 }}></i>
        </div>
      ))}

      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 relative z-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-7 text-white/50 text-xs font-bold">
          <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white/80">Waste Calculator</span>
        </div>

        <div className="max-w-3xl">
          {/* Tag */}
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 thick-border cartoon-shadow-sm anim-bounce-in"
            style={{ background: "#6CC412" }}
          >
            <i className="ri-sparkling-2-line text-white text-xs"></i>
            <span className="text-white text-xs font-black uppercase tracking-widest">Free · Instant · No BS</span>
          </div>

          <h1
            className="font-black text-white leading-[1.0] mb-5"
            style={{ fontSize: "clamp(2.8rem, 5.5vw, 4.8rem)" }}
          >
            How much rubbish
            <br />
            <span
              className="inline-block"
              style={{ color: "#6CC412" }}
            >
              have you actually got?
            </span>
          </h1>

          <p className="text-white/70 text-lg max-w-xl leading-relaxed font-semibold mb-8">
            Pick your rooms, tap your items, and we&apos;ll tell you{" "}
            <span className="text-white font-black">exactly</span> what size van you need.
            No measuring tape. No guessing. No judgment. 😅
          </p>

          {/* Fun stats */}
          <div className="flex flex-wrap gap-5 mb-10">
            {[
              { icon: "ri-award-line", val: "200+", label: "clear-outs done" },
              { icon: "ri-timer-flash-line", val: "60 sec", label: "to get a quote" },
              { icon: "ri-recycle-line", val: "95%+", label: "diverted from landfill" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-3">
                <span
                  className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-white/25 flex-shrink-0"
                  style={{ background: "rgba(255,255,255,0.1)" }}
                >
                  <i className={`${s.icon} text-cl-lime text-base`}></i>
                </span>
                <div>
                  <p className="text-white font-black text-sm leading-none">{s.val}</p>
                  <p className="text-white/50 text-xs font-semibold mt-0.5">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Type switcher */}
          <div
            className="inline-flex rounded-full p-1.5 thick-border"
            style={{
              background: "rgba(255,255,255,0.1)",
              borderColor: "rgba(255,255,255,0.2)",
            }}
          >
            <button
              onClick={() => onTypeChange("home")}
              className={`flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-black transition-all duration-200 whitespace-nowrap cursor-pointer ${
                calcType === "home"
                  ? "bg-white text-cl-dark cartoon-shadow-sm"
                  : "text-white/70 hover:text-white"
              }`}
            >
              <i className="ri-home-heart-line text-base"></i>
              Home / Domestic
            </button>
            <button
              onClick={() => onTypeChange("office")}
              className={`flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-black transition-all duration-200 whitespace-nowrap cursor-pointer ${
                calcType === "office"
                  ? "bg-white text-cl-dark cartoon-shadow-sm"
                  : "text-white/70 hover:text-white"
              }`}
            >
              <i className="ri-building-line text-base"></i>
              Office / Commercial
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
